import { supabase } from '@/integrations/supabase/client';
import { coverCacheKey } from '../../supabase/functions/_shared/bookCover';

/** Answers already fetched this session: a URL, or null for "no verified cover". */
const answered = new Map<string, string | null>();

/**
 * Verified covers for books our catalogue does not hold, keyed by
 * coverCacheKey(title, author). The verified-cover edge function runs the
 * nightly job's rule (supabase/functions/_shared/bookCover.ts) and caches the
 * result server-side, so this is the ONLY way the site should find a cover for
 * a book without a stored one -- never a live Google or Apple search result.
 * Books with no verified cover are simply absent from the returned map.
 */
export async function fetchVerifiedCovers(
  books: Array<{ title: string; author?: string | null }>,
): Promise<Map<string, string>> {
  const found = new Map<string, string>();
  const ask: Array<{ title: string; author: string }> = [];
  for (const b of books) {
    if (!b.title || !b.author) continue;
    const key = coverCacheKey(b.title, b.author);
    if (answered.has(key)) {
      const url = answered.get(key);
      if (url) found.set(key, url);
    } else if (!ask.some((a) => coverCacheKey(a.title, a.author) === key)) {
      ask.push({ title: b.title, author: b.author });
    }
  }
  if (!ask.length) return found;

  const { data, error } = await supabase.functions.invoke('verified-cover', { body: { books: ask } });
  if (error || !data?.covers) return found;
  for (const [key, url] of Object.entries(data.covers as Record<string, string | null>)) {
    answered.set(key, url);
    if (url) found.set(key, url);
  }
  return found;
}

export { coverCacheKey };
